import { useTheme } from '@mui/material/styles';
import { BaseHighchart } from './BaseHighchart';

/*
 * BubbleSeriesChart
 * ------------
 * Displays a bubble (x, y, z) scatter plot using Highcharts.
 * Expects:
 *   data = {
 *     series: Array<{ label: string, points: Array<{ x: number, y: number, z: number }>, color?: string }>
 *   }
 * Cosmetic props:
 *   - legend: 'right' | 'bottom' | 'top' | null/undefined to hide
 *   - xLabel?: string
 *   - yLabel?: string
 *   - zLabel?: string (for tooltip)
 *   - dataLabels: boolean (show value labels on bubbles)
 *
 * The z row in the tooltip is omitted automatically when the resolved z value is null/undefined/''
 * (e.g. when z only drives bubble size and there's no real third metric to display).
 */
export const BubbleSeriesChart = ({
  data,
  legend,
  xLabel,
  yLabel,
  zLabel,
  xAxisIsDate,
  xAxisFormatter,
  yAxisFormatter,
  xTooltipValueFormatter,
  yTooltipValueFormatter,
  zTooltipValueFormatter,
  tooltipHeaderFormatter,
  dataLabels = false,
  yMin0 = true,
  onClick,
}) => {
  const theme = useTheme();
  const dividerColor = theme.palette.divider;
  const labelStyle = { color: theme.palette.text.secondary };

  if (!data || !data.series?.length) return null;

  // Build series
  const highchartsSeries = data.series.map((s, idx) => ({
    id: `${s.label}__${idx}`,
    name: s.label,
    type: 'bubble',
    color: s.color || theme.palette.primary.main,
    data: s.points.map((p, i) => ({ ...p, z: p.z == null || p.z === '' ? '' : p.z, id: `${s.label}_${i}` })),
    marker: {
      fillColor: s.color || theme.palette.primary.main,
      lineColor: theme.palette.background.paper,
      lineWidth: 1,
    },
  }));

  const legendOptions =
    legend == null ? { enabled: false }
    : legend === 'bottom' ? { enabled: true, align: 'center', verticalAlign: 'bottom' }
    : legend === 'top' ? { enabled: true, align: 'center', verticalAlign: 'top' }
    : { enabled: true, layout: 'vertical', align: 'right', verticalAlign: 'middle' };

  const chartSpacing =
    legend === 'bottom' ? [8, 8, 24, 8]
    : legend === 'top' ? [24, 8, 8, 8]
    : [8, 8, 8, 8];

  const options = {
    chart: {
      type: 'bubble',
      zoomType: 'xy',
      spacing: chartSpacing,
      animation: { duration: 300 },
    },
    title: { text: null },
    legend: legendOptions,

    xAxis: {
      gridLineWidth: 1,
      gridLineColor: dividerColor,
      lineColor: dividerColor,
      tickLength: 0,
      title: {
        text: xLabel || null,
        style: labelStyle,
      },
      labels: {
        formatter: xAxisFormatter,
      },
    },
    yAxis: {
      min: yMin0 ? 0 : undefined,
      gridLineWidth: 1,
      gridLineColor: dividerColor,
      title: {
        text: yLabel || null,
        style: labelStyle,
      },
      labels: {
        formatter: yAxisFormatter,
      },
    },

    tooltip: {
      useHTML: true,
      formatter: function () {
        const point = this.point;

        const header = tooltipHeaderFormatter ? tooltipHeaderFormatter(point) : this.series.name;

        const xVal = xTooltipValueFormatter ? xTooltipValueFormatter(point.x, point) : point.x;
        const yVal = yTooltipValueFormatter ? yTooltipValueFormatter(point.y, point) : point.y;
        const zVal = zTooltipValueFormatter ? zTooltipValueFormatter(point.z, point) : point.z;

        // Omit the z row entirely when there's no real z value to show (e.g. z only drives bubble size)
        const zRow = zVal == null || zVal === '' ? '' : `${zLabel || 'z'}: ${zVal}`;

        return `
          <b>${header}</b><br/>
          ${xLabel || 'x'}: ${xVal}<br/>
          ${yLabel || 'y'}: ${yVal}<br/>
          ${zRow}
        `;
      },
    },

    plotOptions: {
      bubble: {
        minSize: 5,
        maxSize: 30,
        animation: { duration: 300 },
        marker: {
          fillOpacity: 0.9,
        },
        dataLabels: {
          enabled: dataLabels,
          format: '{point.z}',
          style: { color: theme.palette.text.primary },
        },
      },
      series: {
        turboThreshold: 0,
        point: {
          events: {
            click: function () {
              if (onClick) {
                onClick(this.series.name, this.options);
              }
            },
          },
        },
      },
    },

    series: highchartsSeries,
  };

  if (xAxisIsDate) {
    options.xAxis.type = 'datetime';
  }

  return <BaseHighchart options={options} />;
};
